/* eslint-disable camelcase */
// @ts-nocheck
import React from 'react';
import uniqBy from 'lodash/uniqBy';
import sortBy from 'lodash/sortBy';
import semverSort from 'semver/functions/sort';
import { styled } from '@mui/material/styles';
import type { Option } from '../components/form-elements/select-type-ahead';
import SelectTypeAhead from '../components/form-elements/select-type-ahead';
import Search from '../components/search';
import { ajax } from '../vendor/jquery';
import type { MembershipAPIResult, Member } from '../models/member';
import type { RolesAPIResult } from '../models/role';
import Alert from '../components/alert';
import Button from '../shared/button';
import Modal from '../components/modals/modal';
import type { Workflow } from '../workflow-builder/api/types';
import { AppContext } from '../hooks/use-app';
import MembersEdit from './members-edit';
import MemberTable from './member-table';
import { AddMemberModal } from './add-member-modal';
import DeleteModal from './delete-modal';

const StyledSearch = styled(Search)(({ theme }) => ({
  width: theme.spacing(39),
  '& .MuiOutlinedInput-root': {
    height: theme.spacing(3.5),
    padding: theme.spacing(0, 1.125),
  },
  '& .MuiOutlinedInput-root .MuiInputBase-input': {
    padding: '0',
    fontSize: theme.typography.body2.fontSize,
  },
  '& .MuiOutlinedInput-notchedOutline': { top: '-1px' },
  '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },
}));

const StyledSelectTypeAhead = styled(SelectTypeAhead)(({ theme }) => ({
  width: theme.spacing(22),
  '& .MuiOutlinedInput-root': {
    height: theme.spacing(3.5),
    padding: '0',
  },
  '& .MuiOutlinedInput-root .MuiAutocomplete-input': {
    padding: '0',
    paddingLeft: theme.spacing(0.625),
  },
}));

interface Props {
  canAddMembers: boolean;
  samlEnabled: boolean;
  isOwner: boolean;
  billableCount: number;
  includedUsers: number;
  availableLicenses: number;
  minLicensePurchase?: number;
}

interface State {
  app_version: Option;
  members: Member[];
  device_model: Option;
  memberOptions: Option[];
  membersImported: boolean;
  modelOptions: Option[];
  platform: Option;
  platformOptions: Option[];
  roleOptions: Option[];
  loading: boolean;
  role: Option;
  roles: any;
  name: string;
  versionOptions: Option[];
  showDeleteModal: boolean;
  showAddMembersModal: boolean;
  currentMember: Member;
  userMemberID: string;
  editingMembershipID: string;
  show: boolean;
  isManaged: boolean;
  showSuccessAlert: boolean;
  successAlertMessage: string;
  workflows: ReadonlyArray<Workflow> | [];
}

const DEFAULT_ROLE = { label: 'All Roles', value: '' };
const DEFAULT_PLATFORM = { label: 'All Platforms', value: '' };
const DEFAULT_VERSION = { label: 'All App Versions', value: '' };
const DEFAULT_MODEL = { label: 'All Models', value: '' };
export const DEFAULT_MEMBER_OPTION = { label: 'Select Member', value: 'default' };

export default class Members extends React.Component<Props, State> {
  static contextType = AppContext;

  state: State = {
    app_version: DEFAULT_VERSION,
    currentMember: null,
    device_model: DEFAULT_MODEL,
    memberOptions: [],
    membersImported: false,
    userMemberID: '',
    editingMembershipID: null,
    loading: true,
    members: [],
    modelOptions: [],
    name: '',
    platform: DEFAULT_PLATFORM,
    platformOptions: [],
    role: DEFAULT_ROLE,
    roles: [],
    roleOptions: [],
    isManaged: true,
    showDeleteModal: false,
    showAddMembersModal: false,
    versionOptions: [],
    show: false,
    showSuccessAlert: false,
    successAlertMessage: '',
    workflows: [],
  };

  fetchTime: number;
  maxMembersReached = false;
  message = '';

  async callMembershipAPI() {
    const fetchTime = this.fetchTime = Date.now();

    const result: MembershipAPIResult = await ajax({
      data: {
        app_version: this.state.app_version.value,
        device_model: this.state.device_model.value,
        platform: this.state.platform.value,
        role_id: this.state.role.value,
        search: this.state.name,
      },
      method: 'GET',
      url: '/api/v2/memberships?exclude_system_users=1&device_info=1&managed_info=1&sort=user',
    });

    if (fetchTime === this.fetchTime) {
      this.setState({
        members: result.memberships,
        loading: false,
      });
    }
  }

  async setMemberOptions() {
    const result: MembershipAPIResult = await ajax({
      method: 'GET',
      url: '/api/v2/memberships?exclude_system_users=1&device_info=1&managed_info=1&sort=user&sort_direction=asc',
    });

    const memberOptions: Option[] = result.memberships.map((membership) => (
      { label: membership.user, value: membership.id }
    ));
    memberOptions.unshift({ label: 'Unassign Records', value: '' });
    memberOptions.unshift(DEFAULT_MEMBER_OPTION);
    const { modelOptions, platformOptions, versionOptions } = this.getDeviceOptions(result.memberships);
    this.setState({
      memberOptions, modelOptions, platformOptions, versionOptions,
    });
  }

  async sendDeleteCall(assignedMember: string, memberName: string) {
    if (assignedMember === '') {
      assignedMember = null;
    }

    await this.context.api.memberships.delete(this.state.currentMember.id, { reassign_to_membership_id: assignedMember });

    await this.callMembershipAPI();

    await this.setMemberOptions();

    window.location.reload();
  }

  async sendUpdateCall() {
    await this.callMembershipAPI();
    window.location.reload();
  }

  async callRoleAPI() {
    const result: RolesAPIResult = await ajax({
      method: 'GET',
      url: '/api/v2/roles.json?all=true',
    });
    const { roles } = result;
    const roleOptions: Option[] = roles.map((role) => ({ label: role.name, value: role.id }));
    roleOptions.sort().unshift(DEFAULT_ROLE);
    this.setState({ roles, roleOptions });
  }

  async callWorkflowsAPI() {
    const result = await ajax({
      method: 'GET',
      url: '/api/v2/workflows.json',
    });

    this.setState({ workflows: result });
  }

  getDeviceOptions = (members: Member[]) => {
    let modelOptions: Option[] = [];
    let platformOptions: Option[] = [];
    let versionOptions: Option[] = [];

    members.map((member) => {
      if (!member.device_info) {
        return null;
      }
      const platform = `${member.device_info.platform} ${member.device_info.platform_version}`;
      const { model } = member.device_info;
      const version = member.device_info.app_version;
      if (
        platform !== null
        && platformOptions.reduce((acc, val) => acc.concat(val), []).indexOf(platform) < 0
      ) {
        platformOptions.push({ label: platform, value: platform });
      }
      if (model !== null && modelOptions.reduce((acc, val) => acc.concat(val), []).indexOf(model) < 0) {
        modelOptions.push({ label: model, value: model });
      }
      if (version !== null && versionOptions.reduce((acc, val) => acc.concat(val), []).indexOf(version) < 0) {
        versionOptions.push({ label: version, value: version });
      }
    });

    const sorterLowercase = (item: Option) => item.value.toLowerCase();

    platformOptions = sortBy(platformOptions, [(option) => sorterLowercase(option)]);
    platformOptions = uniqBy(platformOptions, 'value');
    platformOptions.unshift(DEFAULT_PLATFORM);

    modelOptions = sortBy(modelOptions, [(option) => sorterLowercase(option)]);
    modelOptions = uniqBy(modelOptions, 'value');
    modelOptions.unshift(DEFAULT_MODEL);

    versionOptions = semverSort(versionOptions.map((o) => o.value))
      .map((semver) => ({ label: semver, value: semver }));
    versionOptions = uniqBy(versionOptions, 'value');
    versionOptions.unshift(DEFAULT_VERSION);

    return {
      modelOptions,
      platformOptions,
      versionOptions,
    };
  };

  handleNameSearch = (event: string) => {
    this.setState({ name: event }, this.callMembershipAPI);
  };

  handleEditClick = (memberID: string) => {
    this.setState({
      editingMembershipID: memberID,
      show: true,
      showSuccessAlert: false,
    });
  };

  handleDeleteModalOpenClick = (member) => {
    this.setState({ showDeleteModal: true, currentMember: member });
  };

  handleDeleteModalCloseClick = () => {
    this.setState({ showDeleteModal: false, currentMember: null });
  };

  handleDeleteClick = (assignedMember: string, memberName: string) => {
    this.sendDeleteCall(assignedMember, memberName);
    this.setState({ showDeleteModal: false, currentMember: null });
  };

  handleAddMemberModalOpenClick = (label) => {
    const isManaged = label === 'Add SSO Members';

    this.setState({
      showAddMembersModal: true,
      isManaged,
    });
  };

  handleAddMemberCloseClick = () => {
    if (confirm('You will lose any changes. Are you sure you want to Cancel?')) { // eslint-disable-line no-alert
      this.setState({ showAddMembersModal: false });
    }
  };

  handleRoleSearch = (e: React.SyntheticEvent<Element, Event>, role: Option) => {
    if (role === null) {
      // do nothing
      return;
    }
    this.setState({ role }, this.callMembershipAPI);
  };

  handlePlatformSearch = (e: React.SyntheticEvent<Element, Event>, platform: Option) => {
    if (platform === null) {
      // do nothing
      return;
    }
    this.setState({ platform }, this.callMembershipAPI);
  };

  handleModelSearch = (e: React.SyntheticEvent<Element, Event>, device_model: Option) => {
    if (device_model === null) {
      // do nothing
      return;
    }
    this.setState({ device_model }, this.callMembershipAPI);
  };

  handleVersionSearch = (e: React.SyntheticEvent<Element, Event>, app_version: Option) => {
    if (app_version === null) {
      // do nothing
      return;
    }
    this.setState({ app_version }, this.callMembershipAPI);
  };

  getAddMemberButton = () => {
    if (this.props.canAddMembers) {
      const label: string = this.props.samlEnabled ? 'Add SSO Members' : 'Add Members';

      return (
        <div>
          <Button
            link
            success
            small
            disabled={this.maxMembersReached || this.state.loading}
            tooltip={this.maxMembersReached && this.message}
            tooltipDelay={0}
            onClick={() => this.handleAddMemberModalOpenClick(label)}
          >
            <i className="fa fa-plus" style={{ marginRight: 3 }} />
            {label}
          </Button>
        </div>
      );
    }
  };

  getSAMLAddMembersButton = () => {
    if (
      this.props.canAddMembers
      && this.props.samlEnabled
    ) {
      const label = 'Add Non-SSO Members';

      return (
        <div>
          <Button
            link
            secondary
            small
            disabled={this.maxMembersReached || this.state.loading}
            tooltip={this.maxMembersReached && this.message}
            tooltipDelay={0}
            onClick={() => this.handleAddMemberModalOpenClick(label)}
            attributes={[{ 'data-testid': 'add-non-sso-member-button' }]}
          >
            <i className="fa fa-plus" style={{ marginRight: 3 }} />
            {label}
          </Button>
        </div>
      );
    }
  };

  getAddMemberModal = () => (
    <AddMemberModal
      memberships={this.state.members}
      isManaged={this.state.isManaged}
      isOpen={this.state.showAddMembersModal}
      billableCount={this.props.billableCount}
      includedUsers={this.props.includedUsers}
      minLicensePurchase={this.props.minLicensePurchase}
      availableLicenses={this.props.availableLicenses}
      onClose={this.handleAddMemberCloseClick}
      onSubmit={() => this.handleAddMemberSubmit()}
      hasImports={(membersImported) => this.setState({ membersImported })}
    />
  );

  getDeleteModal = () => (
    <Modal
      slideTop
      hideActions
      title="Delete Member"
      open={this.state.showDeleteModal}
      onClose={this.handleDeleteModalCloseClick}
      maxWidth="sm"
    >
      <DeleteModal
        orgId={this.state.currentMember ? this.context.globals.organization.id : ''}
        userId={this.state.currentMember?.user_id || ''}
        memberId={this.state.currentMember?.id || ''}
        memberName={this.state.currentMember?.user || ''}
        memberEmail={this.state.currentMember?.email || ''}
        memberOptions={this.state.memberOptions}
        onCancel={this.handleDeleteModalCloseClick}
        onDelete={this.handleDeleteClick}
        workflows={this.state.workflows}
      />
    </Modal>
  );

  getSuccessAlert = () => {
    if (this.state.showSuccessAlert) {
      return (
        <Alert
          severity="success"
          sx={{ textAlign: 'center' }}
        >
          {this.state.successAlertMessage}
        </Alert>
      );
    }
  };

  generateExportUrl = () => {
    const data = {
      app_version: this.state.app_version.value,
      device_model: this.state.device_model.value,
      platform: this.state.platform.value,
      role_id: this.state.role.value,
      search: this.state.name,
      tz_offset: new Date().getTimezoneOffset(),
      tz_name: new Date().toLocaleDateString(undefined, { day: '2-digit', timeZoneName: 'short' }).substring(4),
    };
    const params = new URLSearchParams(data).toString();
    return `members/download?${params}`;
  };

  componentDidUpdate(prevProps, prevState) {
    if (this.state.showAddMembersModal !== prevState.showAddMembersModal) {
      if (!this.state.showAddMembersModal && this.state.membersImported) {
        window.location.reload();
      }
    }
  }

  componentDidMount() {
    this.callMembershipAPI();
    this.setMemberOptions();
    this.callRoleAPI();
    this.callWorkflowsAPI();

    const { max_users } = this.context.globals.organization;
    this.maxMembersReached = (max_users && ((this.props.billableCount - this.props.includedUsers) >= max_users));
    this.message = `You already have the maximum of ${max_users} users
        allowed by your plan`;
  }

  render() {
    return (
      <div
        className="container"
        style={{
          width: '100%',
        }}
      >
        <MembersEdit
          show={this.state.show}
          parameters={{ membershipID: this.state.editingMembershipID }}
          onSubmit={() => this.handleSubmit()}
          onCancel={this.handleCancel}
        />

        {this.getSuccessAlert()}
        <div className="refine-container">
          <div className="row row-options row-refine">
            <StyledSearch
              search={this.state.name}
              setGlobalFilter={this.handleNameSearch}
              placeholder="Filter by First Name, Last Name, or Email"
              minLength={1}
            />
            <StyledSelectTypeAhead
              isError={false}
              errorResource="none"
              value={this.state.role}
              options={this.state.roleOptions}
              onChange={this.handleRoleSearch}
            />
            <StyledSelectTypeAhead
              isError={false}
              errorResource="none"
              value={this.state.platform}
              options={this.state.platformOptions}
              onChange={this.handlePlatformSearch}
            />
            <StyledSelectTypeAhead
              isError={false}
              errorResource="none"
              value={this.state.device_model}
              options={this.state.modelOptions}
              onChange={this.handleModelSearch}
            />
            <StyledSelectTypeAhead
              isError={false}
              errorResource="none"
              value={this.state.app_version}
              options={this.state.versionOptions}
              onChange={this.handleVersionSearch}
            />
          </div>
          <div className="button-group member-actions">
            <Button
              link
              affirmOutlined
              small
              title="Export the list of members with the current parameters"
              href={this.generateExportUrl()}
            >
              <i className="fa fa-download" style={{ marginRight: 3 }} />
              Export CSV
            </Button>
            {this.getAddMemberButton()}
            {this.getSAMLAddMembersButton()}
          </div>
        </div>
        <div className="row">
          <MemberTable
            isOwner={this.props.isOwner}
            data={this.state.members}
            userRoles={this.state.roles}
            onEditClick={this.handleEditClick}
            onOpenClick={this.handleDeleteModalOpenClick}
          />
        </div>
        {this.getDeleteModal()}
        {this.getAddMemberModal()}
      </div>
    );
  }

  async handleAddMemberSubmit() {
    this.setState({ showAddMembersModal: false });
    await this.sendUpdateCall();
  }

  async handleSubmit() {
    this.setState({ show: false });
    await this.callMembershipAPI();

    this.setState({
      showSuccessAlert: true,
      successAlertMessage: 'Member has been updated',
      editingMembershipID: null,
    });

    setTimeout(() => {
      this.setState({ showSuccessAlert: false, successAlertMessage: '' });
    }, 2000);
  }

  postMembership(fields: object) {
    return this.context.api.post(fields);
  }

  handleCancel = () => {
    this.setState({ editingMembershipID: null, show: false });
  };
}
